import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography, Paper, Button, Stack, IconButton, Divider } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Logo from '../components/Logo';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const termlyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Smooth scroll to top when page loads
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const existing = document.getElementById('termly-jssdk');
    if (!existing) {
      const js = document.createElement('script');
      js.id = 'termly-jssdk';
      js.src = 'https://app.termly.io/embed-policy.min.js';
      document.body.appendChild(js);
    }
    if (termlyRef.current) {
      termlyRef.current.setAttribute('name', 'termly-embed');
      termlyRef.current.setAttribute('data-id', '1ce12e9a-5a52-4749-981b-6c40ba2f1b74');
    }
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(0deg, #f1fff9, #f1fff900 30%), radial-gradient(circle at 0 0, #5fd5b6 0, #5fd5b600 30%), linear-gradient(180deg, #79dfffb3 -10%, #79dfff00 70%), radial-gradient(circle at 95% 0, #d0f224 0, #5fd5b600 20%)' }}>
      {/* Header - copied from Landing */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={() => { navigate('/'); scrollToTop(); }}
            >
              <Logo height={48} />
            </Box>

            {/* Navigation */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 3 }} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button variant="text" onClick={() => navigate('/')} sx={{ color: 'text.primary', fontWeight: 500, fontSize: { xs: '0.875rem', md: '1rem' }, '&:hover': { background: 'rgba(16, 185, 129, 0.05)' } }}>
                Features
              </Button>
              <Button variant="text" onClick={() => navigate('/')} sx={{ color: 'text.primary', fontWeight: 500, fontSize: { xs: '0.875rem', md: '1rem' }, '&:hover': { background: 'rgba(16, 185, 129, 0.05)' } }}>
                Pricing
              </Button>
              <Button variant="text" onClick={() => navigate('/')} sx={{ color: 'text.primary', fontWeight: 500, fontSize: { xs: '0.875rem', md: '1rem' }, '&:hover': { background: 'rgba(16, 185, 129, 0.05)' } }}>
                Support
              </Button>
              <Button variant="outlined" onClick={() => navigate('/login')} sx={{ borderColor: 'rgba(16, 185, 129, 0.3)', color: '#10B981', fontWeight: 600, px: 3, fontSize: { xs: '0.875rem', md: '1rem' }, '&:hover': { borderColor: '#10B981', background: 'rgba(16, 185, 129, 0.05)' } }}>
                Sign In
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ pt: 16, pb: 8 }}>
        <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
          {/* Back Button */}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              mb: 3,
              borderColor: 'rgba(16, 185, 129, 0.3)',
              color: '#10B981',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#10B981',
                background: 'rgba(16, 185, 129, 0.05)',
              },
            }}
          >
            Back to Home
          </Button>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Your privacy is important to us. Below you can view our full policy.
          </Typography>
          <Box sx={{ p: { xs: 1, md: 2 }, border: '1px solid #e9ecef', borderRadius: 2, backgroundColor: 'white' }}>
            <div ref={termlyRef}></div>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 3 }}>
            Last updated: December 2024
          </Typography>
        </Paper>
      </Container>

      {/* Footer - copied from Landing */}
      <Box id="footer" sx={{ background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)', color: 'white', mt: 12 }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, 
            gap: 4,
            justifyContent: { xs: 'center', md: 'stretch' }
          }}>
            {/* Company Info */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Logo height={32} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.6,
                    mb: 3,
                    maxWidth: { xs: '100%', md: '64%' },
                  }}
                >
                  Transform your social media presence with AI-powered automation. 
                  Engage with your audience 24/7 and grow your brand effortlessly.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                  <IconButton
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#1877F2' },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/socialinone/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#E1306C' },
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.tiktok.com/@socialinone"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#00f2ea' },
                    }}
                  >
                    <Box
                      component="svg"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      sx={{ width: 24, height: 24, fill: 'currentColor' }}
                    >
                      <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z"/>
                    </Box>
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.youtube.com/@SocialinOne"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#FF0000' },
                    }}
                  >
                    <YouTubeIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            {/* Support */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Support
              </Typography>
              <Stack spacing={1} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/faq')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  FAQ
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/contact')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  component={Link}
                  to="/help-center"
                  variant="text"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Help Center
                </Button>
              </Stack>
            </Box>




            {/* Contact Info */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Contact
              </Typography>
              <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <EmailIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    info@socialinone.ai
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <LocationIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    Almere, Netherlands
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Bottom Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'center' },
              gap: 2,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              © 2025 NowUp. All rights reserved.
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 3 },
                ml: { xs: 0, md: 0 },
              }}
            >
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/terms-of-use')}
              >
                Terms of Service
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/cookie-policy')}
              >
                Cookie Policy
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
